import { useRecoilCallback } from "recoil";
import { folderTreeState } from "../store/folderTree.store";
import { FolderNode, FolderTree } from "../../api-client-nswag/custompermission-client";
import { usersPerSiteSelector } from "../selectors/legalEntities.selectors";

export const useFolderTreeActions = () => {
  const loadFolderTree = useRecoilCallback(
    ({ set }) =>
      (folderTree: any) => {
        set(folderTreeState, folderTree);
      },
    [],
  );

  const updateFolderNode = useRecoilCallback(
    ({ set }) =>
      (
        folderId: number,
        updatedUsers: string[]
      ) => {
        set(folderTreeState, (prev: FolderTree) => {
          if (!Array.isArray(prev)) return prev;

          const removeFromSubtree = (
            node: FolderNode,
            toRemove: Set<string>
          ): FolderNode => {
            const nextUsers = node.users.filter((u) => !toRemove.has(u));

            return {
              ...node,
              users: nextUsers,
              usersCount: nextUsers?.length,
              children: node.children.map((child) =>
                removeFromSubtree(child, toRemove)
              ),
            };
          };

          const walk = (node: FolderNode): FolderNode => {
            if (node.folderId === folderId) {
              const previousUsers = node.users ?? [];
              const updatedSet = new Set(updatedUsers);

              const removed = previousUsers.filter((u) => !updatedSet.has(u));
              const toRemove = new Set(removed);

              const updatedNode: FolderNode = {
                ...node,
                users: updatedUsers,
                usersCount: updatedUsers?.length,
              };

              if (toRemove.size === 0) {
                return {
                  ...updatedNode,
                  children: node.children.map(walk),
                };
              }

              return removeFromSubtree(updatedNode, toRemove);
            }

            return {
              ...node,
              children: node.children.map(walk),
            };
          };

          return prev.map(walk);
        });
      },
    []
  );

  const setFolderTreeToAllUsers = useRecoilCallback(
    ({ set }) =>
      (allUsers: string[]) => {
        set(folderTreeState, (prev: FolderTree) => {
          if (!Array.isArray(prev)) return prev;

          const visit = (node: FolderNode): FolderNode => ({
            ...node,
            users: [...allUsers],
            usersCount: allUsers.length,
            children: (node.children ?? []).map(visit),
          });

          return prev.map(visit);
        });
      },
    []
  );

  const revalidateFolderIsRestrictedByParent = useRecoilCallback(
    ({ set, snapshot }) =>
      async ({ targetLevel = 1 }: { targetLevel?: number }) => {
        const tree = await snapshot.getPromise(folderTreeState);
        const usersPerSite = await snapshot.getPromise(usersPerSiteSelector);

        if (!tree?.length) return;

        const siteUsers = (usersPerSite ?? []).map(user => String(user.userId));
        const siteUserSet = new Set(siteUsers);

        const hasAllSiteUsers = (users: Array<string | number> = []): boolean => {
          const folderUserSet = new Set(users.map(String));

          return (
            folderUserSet.size === siteUserSet.size &&
            siteUsers.every(userId => folderUserSet.has(userId))
          );
        };

        const updateFolderRestrictions = (nodes: FolderNode[], parentRestricted: boolean): FolderNode[] =>
          nodes.map(node => {
            const currentFolderInvalid = !hasAllSiteUsers(node.users);
            const isRestricted = parentRestricted || currentFolderInvalid;

            return {
              ...node,
              isRestricted,
              children: updateFolderRestrictions(node.children ?? [], isRestricted),
            };
          });

        const process = (nodes: FolderNode[], level: number, parentRestricted = false): FolderNode[] => nodes.map(node => {
          if (level === targetLevel) {
            const currentFolderInvalid = !hasAllSiteUsers(node.users);
            const isRestricted = parentRestricted || currentFolderInvalid;

            return {
              ...node,
              isRestricted,
              children: updateFolderRestrictions(node.children ?? [], isRestricted),
            };
          }

          return {
            ...node,
            children: process(node.children ?? [], level + 1, node.isRestricted),
          };
        });

        if (targetLevel === 0) {
          const nextTree = updateFolderRestrictions(tree, false);
          set(folderTreeState, nextTree);
          return;
        }

        const nextTree = process(tree, 0);

        set(folderTreeState, nextTree);
      },
    []
  );

  return {
    loadFolderTree,
    updateFolderNode,
    setFolderTreeToAllUsers,
    revalidateFolderIsRestrictedByParent
  };
};
